import {
  ASSET_TYPE,
  IDocumentMediaPreviewData,
  IMessage,
  MESSAGE_CONTENT_TYPE,
} from "@home-health-notify/domain";
import {
  CircularProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from "@mui/material";
import {
  ChatContext,
  formatChatMessageTime,
  useGetChatAssets,
} from "hhn-commons";
import * as React from "react";
import { useContext } from "react";
import EmptyRecordView from "../../../../../common/EmptyRecordView";
import ScrollListContainer from "../../../../../common/ScrollListContainer";
import { UserFullName } from "../../../../../common/user/UserText";
import { DocumentMediaViewerContext } from "../../../../../context/DocumentMediaViewerContext";
import AudioThumb from "../../../../../message/mediaViewer/mediaThumb/AudioThumb";
import DisplayThumbImage from "../../../../../message/mediaViewer/mediaThumb/DisplayThumbImage";
import VideoThumb from "../../../../../message/mediaViewer/mediaThumb/VideoThumb";
import { MediaAssetItemStyle } from "./MediaAssets.style";

const MediaAssets = () => {
  const {
    // assets: media,
    isChatAssetsLoading,
    getChatAssets,
  } = useGetChatAssets(ASSET_TYPE.MEDIA);
  const { chat } = useContext(ChatContext);

  return (
    <ScrollListContainer
      dataTestId={"asset-" + ASSET_TYPE.MEDIA.toLowerCase()}
      isLoading={isChatAssetsLoading}
      restTop={115}
      onScrollCallback={getChatAssets}
    >
      <>
        <ImageList
          sx={{ width: "100%", p: 1, m: 0 }}
          cols={2}
          rowHeight={170}
          gap={10}
          data-testid="asset-media-list"
        >
          {chat?.groupAssets &&
            chat.groupAssets.data.map(message => {
              const messageData = message.getMessage();
              return (
                <React.Fragment key={messageData.id}>
                  {messageData.contentType === MESSAGE_CONTENT_TYPE.IMAGE &&
                    messageData.images &&
                    messageData.images.length > 0 && (
                      <>
                        {messageData.images.map(image => (
                          <MediaAssetItem
                            message={message}
                            key={messageData.id}
                            mediaList={chat.groupAssets.data}
                          >
                            <DisplayThumbImage
                              imageData={image}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "10px",
                              }}
                            />
                          </MediaAssetItem>
                        ))}
                      </>
                    )}
                  {messageData.contentType === MESSAGE_CONTENT_TYPE.VOICE &&
                    messageData.voiceMessage && (
                      <MediaAssetItem
                        message={message}
                        key={messageData.id}
                        mediaList={chat.groupAssets.data}
                      >
                        <AudioThumb voiceMessage={messageData.voiceMessage} />
                      </MediaAssetItem>
                    )}
                  {messageData.contentType === MESSAGE_CONTENT_TYPE.VIDEO &&
                    messageData.videos &&
                    messageData.videos.length > 0 && (
                      <>
                        {messageData.videos.map(video => (
                          <MediaAssetItem
                            message={message}
                            key={messageData.id}
                            mediaList={chat.groupAssets.data}
                          >
                            <VideoThumb
                              videoData={video}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "10px",
                              }}
                            />
                          </MediaAssetItem>
                        ))}
                      </>
                    )}
                </React.Fragment>
              );
            })}
          {isChatAssetsLoading && (
            <ImageListItem
              cols={2}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "170px",
              }}
            >
              <CircularProgress />
            </ImageListItem>
          )}
        </ImageList>
        {!isChatAssetsLoading &&
          chat?.groupAssets &&
          chat.groupAssets.data.length <= 0 && <EmptyRecordView />}
      </>
    </ScrollListContainer>
  );
};

interface IMediaAssetItemProp {
  message: IMessage;
  mediaList: IMessage[];
  children: React.ReactNode;
}

const MediaAssetItem = ({
  message,
  children,
  mediaList,
}: IMediaAssetItemProp) => {
  const { openDocumentMediaViewer } = useContext(DocumentMediaViewerContext);

  const onMediaMessageClicked = () => {
    const previewMedia: IDocumentMediaPreviewData =
      message.getPreviewDataFromMessage()!;
    const previewMediaList: IDocumentMediaPreviewData[] = mediaList.map(
      message => message.getPreviewDataFromMessage()!,
    );
    openDocumentMediaViewer(previewMedia, previewMediaList);
  };
  const messageData = message.getMessage();

  return (
    <MediaAssetItemStyle
      key={messageData.id}
      data-testid="asset-media-list-item"
      onClick={onMediaMessageClicked}
    >
      {children}
      <ImageListItemBar
        sx={{
          borderRadius: "0 0 10px 10px",
        }}
        title={<UserFullName userId={messageData.from} />}
        subtitle={formatChatMessageTime(messageData.createdAt)}
      />
    </MediaAssetItemStyle>
  );
};

export default MediaAssets;
